import { memo, type RefObject } from 'react';

import { FolderTreeIcon, UploadIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { UploadZoneOverlay } from '~/components/PrivateDropzone/components/UploadZoneOverlay';
import { type DropZone } from '~/components/PrivateDropzone/types';
import { useSubSideNavPathname } from '~/components/v2/components/workspace/sub-side-nav/useSubSideNavPathname';
import { useSubSideNavStore } from '~/components/v2/store/features/workspace/useSubSideNavStore';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { isSubSideNavOpen } from '~/utils/isSubSideNavOpen';

export type MultipleZoneOverlayProps = {
  hoveredZone: DropZone | null;
  currentBoardTitle?: string;
  currentWorkspaceName?: string;
  dropZoneChooseBoardRef: RefObject<HTMLDivElement>;
  dropZoneUploadRef: RefObject<HTMLDivElement>;
};

export const MultipleZoneOverlay = memo(
  ({
    hoveredZone,
    currentBoardTitle,
    currentWorkspaceName,
    dropZoneChooseBoardRef,
    dropZoneUploadRef,
  }: MultipleZoneOverlayProps) => {
    // Pre-ui2026 uses the sidebar store. ui2026 collapses the sub-nav in its own store while that
    // flag stays open. `--sidebar-width` tweens to 0, so reading it during render can catch a
    // mid-collapse width and keep the "different board" zone mounted.
    const legacySidebarOpen = useWorkspaceSidebarStore((state) => state.open);
    const { subSideNavPathname } = useSubSideNavPathname();
    const subNavStoreOpen = useSubSideNavStore((state) => state.open);
    const ui2026Chrome = typeof document !== 'undefined' && document.documentElement.dataset.ui2026 !== undefined;
    const isSidebarOpen = ui2026Chrome ? isSubSideNavOpen(subSideNavPathname, subNavStoreOpen) : legacySidebarOpen;

    return (
      <div
        data-testid="MULTIPLE_ZONE_OVERLAY_CONTENT"
        // In-flow main rail width (`--main-side-nav-width`, 60px collapsed / 232px pinned). Page pad is spacing.1, zone inset is spacing.2.
        // `--workspace-agent-panel-slot-width` is the in-flow agent spacer plus ui2026 gutter (0 when minimized).
        className="absolute top-18 right-2 ui2026:right-[calc(theme(spacing.1)+var(--workspace-agent-panel-slot-width,0px)+theme(spacing.2))] bottom-2 left-2 ui2026:left-[calc(var(--main-side-nav-width,60px)+theme(spacing.1)+theme(spacing.2))] flex gap-3 ui2026:gap-4"
      >
        {(isSidebarOpen || !ui2026Chrome) && (
          <UploadZoneOverlay
            className={cn(
              'px-4 py-8 transition-all duration-200',
              isSidebarOpen
                ? 'ui2026:w-[calc(var(--sidebar-width)-1rem)] w-[calc(var(--sidebar-width)-0.75rem)]'
                : 'w-full',
            )}
            data-testid="MULTIPLE_ZONE_OVERLAY_LEFT_ZONE"
            description={`Choose where to upload your files in ${currentWorkspaceName || 'this workspace'}`}
            icon={isSidebarOpen ? FolderTreeIcon : UploadIcon}
            isActive={hoveredZone === 'choose-board'}
            title={isSidebarOpen ? 'Upload to a different board' : 'Drop files here to upload'}
            zoneRef={dropZoneChooseBoardRef}
          />
        )}
        {(isSidebarOpen || ui2026Chrome) && (
          <UploadZoneOverlay
            className={cn('transition-all duration-200', isSidebarOpen ? 'flex-1' : 'w-full')}
            data-testid="MULTIPLE_ZONE_OVERLAY_RIGHT_ZONE"
            description="Automatically upload your assets and files to this board"
            icon={UploadIcon}
            isActive={hoveredZone === 'upload'}
            title={`Drop files here to upload to ${currentBoardTitle || 'this board'}`}
            zoneRef={dropZoneUploadRef}
          />
        )}
      </div>
    );
  },
);

MultipleZoneOverlay.displayName = 'MultipleZoneOverlay';
